import { useMemo } from "react"
import {
  money,
  toRevenueRows,
  type RevenueStepRow,
} from "../../../revenueByJourneyStep"

/*
    Revenue by journey step, with the two numbers behind it (MPN-1836).

    It used to show a dollar amount per step and nothing else, so checking one meant opening
    the Pricesheet page, reading the step's price and dividing: $3,150 / $450 = 7. The count
    and the price were already in the payload and were being dropped.

    A table rather than the old flex list, because three numbers per row need columns to line
    up under headings; a row of loose spans stops being readable at the second column.
*/
export const RevenueByJourneyStep = ({ data }: { data: any }) => {
  const rows = useMemo(() => toRevenueRows(data), [data])

  if (rows.length === 0) {
    return <p className="text-muted mb-0">No journey step data available.</p>
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr className="text-muted small">
            <th>Journey step</th>
            <th className="text-end"># Conversions</th>
            <th className="text-end">Step price</th>
            <th className="text-end">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: RevenueStepRow) => (
            <tr key={row.key}>
              <td className="fw-semibold">{row.name}</td>
              <td className="text-end">{row.conversions.toLocaleString()}</td>
              <td className="text-end">
                {money(row.pricePerConversion)}
                {/* What a conversion earns is the base price plus the step's own. Said out
                    loud only when a base price is actually in play, so the common case stays
                    a single number. */}
                {row.basePrice !== null && (
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    incl. {money(row.basePrice)} base
                  </div>
                )}
              </td>
              <td className="text-end">
                ${row.revenue.toLocaleString()}
                {/* The reconciliation check Rachel asked about. It fires only when the row
                    genuinely does not add up, which means the events behind one step were
                    not all priced the same -- the discrepancy this ticket came out of. */}
                {row.mismatched && (
                  <div
                    className="text-warning-emphasis"
                    style={{ fontSize: 11 }}
                    title={
                      `${row.conversions.toLocaleString()} x ${money(row.pricePerConversion)}`
                      + ` is ${money(row.conversions * row.pricePerConversion)}, not this.`
                      + ` The conversions behind this step were not all priced the same.`
                    }
                  >
                    does not add up
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
